import { useCallback, useEffect, useRef, useState } from 'react';
import { getCameraGanttV2 } from '../../../services/statistics';
import {
  GANTT_REQUEST_CONCURRENCY,
  buildGanttParams,
  describeGanttLimit,
  ganttSegments,
  isAbortError,
  isGanttLimitError,
  runWithConcurrency,
} from './ganttV2Model';

/*
 * §19.2 (FE-FIX16): «отменять stale request group при смене filters и
 * использовать bounded/consolidated loading state».
 *
 * Запросы панелей живут группой. Любое изменение фильтра (scope, период,
 * метрика, режим) обрывает прежнюю группу AbortController'ом и начинает новую с
 * чистым состоянием, поэтому ответ по старому выбору не может дорисоваться к
 * новому: он либо отменён, либо отброшен по флагу поколения.
 *
 * Загрузка ограничена по параллельности — быстрые клики по чекбоксам не
 * превращаются в шторм запросов.
 */

const EMPTY = { panels: {}, loading: false, loaded: 0, total: 0 };

export const useGanttV2Panels = ({ targets, metric, fromIso, toIso, mode, bucketSeconds }) => {
  const [state, setState] = useState(EMPTY);
  const groupRef = useRef(null);
  const retryRef = useRef(() => {});

  // Ключ группы: полное описание запроса. Новая ссылка на тот же выбор
  // перезапускать загрузку не должна.
  const groupKey = JSON.stringify({
    targets: (targets || []).map((target) => [target.cameraId, target.zoneId]),
    metric,
    fromIso,
    toIso,
    mode,
    bucketSeconds,
  });

  useEffect(() => {
    const group = JSON.parse(groupKey);
    if (groupRef.current) groupRef.current.abort();

    if (!group.metric || !group.fromIso || !group.toIso || group.targets.length === 0) {
      groupRef.current = null;
      setState(EMPTY);
      return undefined;
    }

    const controller = new AbortController();
    groupRef.current = controller;
    let cancelled = false;

    setState({ panels: {}, loading: true, loaded: 0, total: group.targets.length });

    const request = (cameraId, zoneId, mode) => getCameraGanttV2(
      cameraId,
      buildGanttParams({
        metric: group.metric,
        fromIso: group.fromIso,
        toIso: group.toIso,
        zoneId,
        mode,
        bucketSeconds: group.bucketSeconds,
      }),
      { signal: controller.signal }
    );

    const load = async ([cameraId, zoneId], isRetry = false) => {
      const key = zoneId ? `${cameraId}:${zoneId}` : String(cameraId);
      try {
        let data;
        let fallback = false;
        try {
          data = await request(cameraId, zoneId, group.mode);
        } catch (error) {
          // «Кадров» за период больше, чем отдаёт сервер, даже на потолке
          // max_points: панель строится интервалами и помечается, вместо
          // того чтобы оставить пользователя с ошибкой на одной камере.
          if (group.mode !== 'raw' || !isGanttLimitError(error)) throw error;
          data = await request(cameraId, zoneId, 'compressed');
          fallback = true;
        }
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loaded: isRetry ? prev.loaded : prev.loaded + 1,
          panels: { ...prev.panels, [key]: { status: 'ready', data, fallback, segments: ganttSegments(data) } },
        }));
      } catch (error) {
        if (cancelled || isAbortError(error)) return;
        setState((prev) => ({
          ...prev,
          loaded: isRetry ? prev.loaded : prev.loaded + 1,
          panels: { ...prev.panels, [key]: { status: 'error', error: describeGanttLimit(error) } },
        }));
      }
    };

    // «Повторить» перезапускает одну панель в рамках той же группы: остальные
    // панели и счётчик загрузки не затрагиваются.
    retryRef.current = (key) => {
      const target = group.targets.find(([cameraId, zoneId]) => (
        (zoneId ? `${cameraId}:${zoneId}` : String(cameraId)) === key
      ));
      if (!target || cancelled) return;
      setState((prev) => {
        const { [key]: _removed, ...rest } = prev.panels;
        return { ...prev, panels: rest };
      });
      load(target, true);
    };

    runWithConcurrency(group.targets, GANTT_REQUEST_CONCURRENCY, load).then(() => {
      if (!cancelled) setState((prev) => ({ ...prev, loading: false }));
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [groupKey]);

  const retry = useCallback((key) => retryRef.current(key), []);

  return { ...state, retry };
};

export default useGanttV2Panels;
